"use client"

import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { useTranslations } from "next-intl"
import { CheckNewIcon, Floor2Icon, Home2Icon, LocationIcon, NewText } from "ui"
import { Apartment, replaceMediaUrls, wrapTables } from "utils"

const SpecsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const AddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const SpecsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
`

const StatsColumn = styled.div`
  display: flex;
  flex-direction: column;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 158px;
    flex-shrink: 0;
  }
`

const CheckmarksGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: contents;
  }
`

const StyledBuildingCharacteristicsText = styled(NewText)`
  * {
    &:first-child {
      margin-top: 0 !important;
    }
    &:last-child {
      margin-bottom: 0 !important;
    }
  }
`

const Char = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
`

type Props = {
  apartment: Apartment
  buildingCharacteristicsText?: string
}
function BuildingFeatures({ apartment, buildingCharacteristicsText }: Props) {
  const { colors } = useTheme()
  const t = useTranslations("AccordionDetails")
  return (
    <SpecsContainer>
      {apartment.address && (
        <AddressRow>
          <LocationIcon fill="#011727" style={{ flexShrink: 0 }} />
          <NewText
            component={"p"}
            desktopFontSize={20}
            color={colors.newBlack}
            desktopLineHeight={"1.2"}
            mobileFontSize={20}
            desktopFontWeight={700}
            mobileFontWeight={700}
            mobileLineHeight={"1.2"}
            sx={{
              margin: 0,
            }}
          >
            {apartment.address}
            {apartment.city ? `, ${apartment.city}` : ""}
            {apartment.buildingTitle && ` | ${t("building")} ${apartment.buildingTitle}`}
          </NewText>
        </AddressRow>
      )}
      {apartment.generalProjectInfo && (
        <NewText
          component={"p"}
          desktopFontSize={16}
          color={colors.newBlack}
          desktopLineHeight={"normal"}
          mobileFontSize={16}
          mobileFontWeight={400}
          desktopFontWeight={400}
          mobileLineHeight={"normal"}
        >
          {apartment.generalProjectInfo}
        </NewText>
      )}
      {buildingCharacteristicsText && (
        <StyledBuildingCharacteristicsText
          component={"div"}
          desktopFontSize={16}
          color={colors.newBlack}
          desktopLineHeight={"normal"}
          mobileFontSize={16}
          mobileFontWeight={400}
          desktopFontWeight={400}
          mobileLineHeight={"normal"}
          dangerouslySetInnerHTML={{
            __html: wrapTables(replaceMediaUrls(buildingCharacteristicsText)),
          }}
        />
      )}
      <SpecsGrid>
        {/* Right column: building stats with icons */}
        <StatsColumn>
          {/* Compare against 0 rather than testing truthiness: these are numbers,
              so a plain `value && ...` renders a bare 0 into the DOM whenever the
              building has no usable stat, which is what showed up as a stray "0"
              for an apartment on floor 0. */}
          {(apartment.numberOfApartments ?? 0) > 0 && (
            <Char>
              <Home2Icon style={{ flexShrink: 0, width: 20, height: 20 }} color={colors.newBlack} />
              <NewText
                component={"span"}
                desktopFontSize={16}
                color={colors.newBlack}
                desktopLineHeight={"normal"}
                mobileFontSize={16}
                mobileFontWeight={400}
                desktopFontWeight={400}
                mobileLineHeight={"normal"}
                sx={{ whiteSpace: "nowrap" }}
              >
                {apartment.numberOfApartments} {t("apartments")}
              </NewText>
            </Char>
          )}
          {(apartment.numberOfFloors ?? 0) > 0 && (
            <Char>
              <Floor2Icon
                style={{ flexShrink: 0, width: 20, height: 20 }}
                color={colors.newBlack}
              />
              <NewText
                component={"span"}
                desktopFontSize={16}
                color={colors.newBlack}
                desktopLineHeight={"normal"}
                mobileFontSize={16}
                mobileFontWeight={400}
                desktopFontWeight={400}
                mobileLineHeight={"normal"}
                sx={{ whiteSpace: "nowrap" }}
              >
                {apartment.numberOfFloors} {t("floors")}
              </NewText>
            </Char>
          )}
        </StatsColumn>
        <CheckmarksGrid>
          {/* Middle column: checkmarks (first half) */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {apartment.buildingCharacteristics
              .slice(0, Math.ceil(apartment.buildingCharacteristics.length / 2))
              .map((el, i) => (
                <Char key={`mid-${i}`}>
                  <CheckNewIcon style={{ flexShrink: 0 }} />
                  <NewText
                    component={"span"}
                    desktopFontSize={16}
                    color={colors.newBlack}
                    desktopLineHeight={"normal"}
                    mobileFontSize={16}
                    mobileFontWeight={400}
                    desktopFontWeight={400}
                    mobileLineHeight={"normal"}
                  >
                    {el}
                  </NewText>
                </Char>
              ))}
          </div>
          {/* Left column: checkmarks (second half) */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {apartment.buildingCharacteristics
              .slice(Math.ceil(apartment.buildingCharacteristics.length / 2))
              .map((el, i) => (
                <Char key={`left-${i}`}>
                  <CheckNewIcon style={{ flexShrink: 0 }} />
                  <NewText
                    component={"span"}
                    desktopFontSize={16}
                    color={colors.newBlack}
                    desktopLineHeight={"normal"}
                    mobileFontSize={16}
                    mobileFontWeight={400}
                    desktopFontWeight={400}
                    mobileLineHeight={"normal"}
                  >
                    {el}
                  </NewText>
                </Char>
              ))}
          </div>
        </CheckmarksGrid>
      </SpecsGrid>
    </SpecsContainer>
  )
}
export default BuildingFeatures
